import { FormEvent, useEffect, useState } from "react";
import { Eye, EyeOff, Lock, Mail, Phone, Send, ShieldCheck, UserRound, X } from "lucide-react";
import { api, type AuthSessionState, type User } from "../services/api";
import { type Language } from "../types/siteContent";
import { LegalDocumentModal } from "./LegalDocumentModal";
import { type LegalDocumentKind } from "../content/legalDocuments";

type AuthMode = "login" | "register" | "forgot";

const REMEMBERED_ACCOUNT_KEY = "maipo-auth-account";
const REMEMBER_ACCOUNT_ENABLED_KEY = "maipo-auth-remember-account";
const REMEMBER_PASSWORD_ENABLED_KEY = "maipo-auth-remember-password";

type Props = {
  open: boolean;
  language: Language;
  initialMode?: "login" | "register";
  bodyOverride?: string;
  canvasSession?: boolean;
  onClose: () => void;
  onAuthenticated: (user: User, session: AuthSessionState) => void;
};

export function AuthModal({ open, language, initialMode = "login", bodyOverride, canvasSession = false, onClose, onAuthenticated }: Props) {
  const copy = language === "en"
    ? {
        close: "Close",
        eyebrow: "Account",
        titleLogin: "Sign in",
        titleRegister: "Create account",
        titleForgot: "Reset password",
        body: "Sign in to download the latest installer.",
        forgotBody: "Enter your account email and verification code to set a new password.",
        loginTab: "Sign in",
        registerTab: "Register",
        resetTab: "Reset password",
        account: "Email or phone",
        email: "Email",
        nickname: "Nickname",
        password: "Password",
        newPassword: "New password",
        confirmPassword: "Confirm password",
        code: "Verification code",
        sendCode: "Send code",
        sendingCode: "Sending",
        codeSent: "Code sent",
        resetCodeSent: "If this email is registered, a verification code has been sent.",
        resetSuccess: "Password reset. Please sign in with your new password.",
        agreementText: "I have read and agree to",
        agreementAnd: "and",
        terms: "User Agreement",
        privacy: "Privacy Policy",
        agreementError: "Please read and agree to the user agreement and privacy policy first.",
        accountPlaceholder: "Email or phone number",
        emailPlaceholder: "name@example.com",
        nicknamePlaceholder: "Optional",
        passwordPlaceholder: "At least 8 characters",
        newPasswordPlaceholder: "Enter a new password",
        confirmPlaceholder: "Enter password again",
        codePlaceholder: "6-digit code",
        showPassword: "Show password",
        hidePassword: "Hide password",
        showConfirmPassword: "Show confirm password",
        hideConfirmPassword: "Hide confirm password",
        working: "Working",
        accountError: "Please enter your email or phone.",
        emailError: "Please enter a valid email.",
        passwordError: "Password must be at least 8 characters.",
        confirmError: "Passwords do not match.",
        codeRequiredError: "Please enter the verification code.",
        requestError: "Request failed",
        autoLogin: "Auto login",
        rememberPassword: "Remember password",
        registerLink: "No account? Create one",
        loginLink: "Already have an account? Sign in",
        forgotLink: "Forgot password?"
      }
    : {
        close: "关闭",
        eyebrow: "Account",
        titleLogin: "登录",
        titleRegister: "创建账号",
        titleForgot: "重置密码",
        body: "登录后即可下载最新版本安装包。",
        forgotBody: "请输入账号邮箱、验证码和新密码。",
        loginTab: "登录",
        registerTab: "注册",
        resetTab: "重置密码",
        account: "邮箱或手机号",
        email: "邮箱",
        nickname: "昵称",
        password: "密码",
        newPassword: "新密码",
        confirmPassword: "确认密码",
        code: "验证码",
        sendCode: "发送验证码",
        sendingCode: "发送中",
        codeSent: "验证码已发送",
        resetCodeSent: "如果该邮箱已注册，验证码已发送，请查收。",
        resetSuccess: "密码已重置，请使用新密码登录。",
        agreementText: "我已阅读并同意",
        agreementAnd: "和",
        terms: "用户协议",
        privacy: "隐私政策",
        agreementError: "请先阅读并同意用户协议和隐私政策。",
        accountPlaceholder: "邮箱或手机号",
        emailPlaceholder: "name@example.com",
        nicknamePlaceholder: "可选",
        passwordPlaceholder: "至少 8 位字符",
        newPasswordPlaceholder: "请输入新密码",
        confirmPlaceholder: "再次输入密码",
        codePlaceholder: "6 位验证码",
        showPassword: "显示密码",
        hidePassword: "隐藏密码",
        showConfirmPassword: "显示确认密码",
        hideConfirmPassword: "隐藏确认密码",
        working: "处理中",
        accountError: "请输入邮箱或手机号。",
        emailError: "请输入有效邮箱。",
        passwordError: "密码至少需要 8 位字符。",
        confirmError: "两次输入的密码不一致。",
        codeRequiredError: "请输入验证码。",
        requestError: "请求失败",
        autoLogin: "自动登录",
        rememberPassword: "记住密码",
        registerLink: "没有账户？点击注册",
        loginLink: "已有账户？点击登录",
        forgotLink: "忘记密码？"
      };

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [account, setAccount] = useState("");
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeMessage, setCodeMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementModal, setAgreementModal] = useState<LegalDocumentKind | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(() => localStorage.getItem(REMEMBER_PASSWORD_ENABLED_KEY) === "true");
  const [rememberAccount] = useState(true);

  useEffect(() => {
    if (open) setMode(canvasSession ? "login" : initialMode);
  }, [canvasSession, initialMode, open]);

  useEffect(() => {
    if (!open || mode !== "login" || !rememberAccount) return;
    setAccount(localStorage.getItem(REMEMBERED_ACCOUNT_KEY) || "");
  }, [mode, open, rememberAccount]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setCodeMessage(null);

    const normalizedAccount = account.trim();
    if (!normalizedAccount) {
      setError(mode === "register" ? copy.emailError : copy.accountError);
      setLoading(false);
      return;
    }

    if (mode !== "login" && !isEmailAccount(normalizedAccount)) {
      setError(copy.emailError);
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError(copy.passwordError);
      setLoading(false);
      return;
    }

    if (mode !== "login" && password !== confirmPassword) {
      setError(copy.confirmError);
      setLoading(false);
      return;
    }

    if (mode !== "forgot" && !agreementAccepted) {
      setError(copy.agreementError);
      setLoading(false);
      return;
    }

    if (mode !== "login" && !verificationCode.trim()) {
      setError(copy.codeRequiredError);
      setLoading(false);
      return;
    }

    try {
      if (mode === "forgot") {
        await api.resetPassword({
          email: normalizedAccount,
          password,
          verificationCode: verificationCode.trim()
        });
        setModeSafely("login");
        setCodeMessage(copy.resetSuccess);
        return;
      }

      const result = mode === "login"
        ? await api.login({
          account: normalizedAccount,
          password,
          rememberMe: canvasSession ? false : rememberMe,
          canvasSession,
          termsAccepted: true,
          privacyAccepted: true,
          agreementVersion: "v1"
        })
        : await api.register({
          email: normalizedAccount,
          nickname: nickname.trim() || undefined,
          password,
          verificationCode: verificationCode.trim() || undefined,
          termsAccepted: true,
          privacyAccepted: true,
          agreementVersion: "v1"
        });

      if (mode === "login") {
        localStorage.setItem(REMEMBER_ACCOUNT_ENABLED_KEY, String(rememberAccount));
        localStorage.setItem(REMEMBER_PASSWORD_ENABLED_KEY, String(rememberPassword));
        if (rememberAccount) {
          localStorage.setItem(REMEMBERED_ACCOUNT_KEY, normalizedAccount);
        } else {
          localStorage.removeItem(REMEMBERED_ACCOUNT_KEY);
        }
      }

      onAuthenticated(result.user, { rememberMe: result.rememberMe });
      resetForm();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : copy.requestError);
    } finally {
      setLoading(false);
    }
  }

  async function handleSendCode() {
    const normalizedAccount = account.trim();
    setError(null);
    setCodeMessage(null);
    if (!normalizedAccount || !isEmailAccount(normalizedAccount)) {
      setError(copy.emailError);
      return;
    }

    if (mode === "register" && !agreementAccepted) {
      setError(copy.agreementError);
      return;
    }

    setSendingCode(true);
    try {
      await api.sendCode({
        account: normalizedAccount,
        accountType: "EMAIL",
        purpose: mode === "forgot" ? "RESET_PASSWORD" : "REGISTER"
      });
      setCodeMessage(mode === "forgot" ? copy.resetCodeSent : copy.codeSent);
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : copy.requestError);
    } finally {
      setSendingCode(false);
    }
  }

  function resetForm() {
    setNickname("");
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setAgreementAccepted(false);
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setRememberMe(false);
    setError(null);
    setCodeMessage(null);
  }

  function setModeSafely(nextMode: AuthMode) {
    if (mode === nextMode) return;
    setMode(nextMode);
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setAgreementAccepted(false);
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setRememberMe(false);
    setError(null);
    setCodeMessage(null);
  }

  const accountLabel = mode === "login" ? copy.account : copy.email;
  const accountIcon = mode === "login" && account.trim() && inferAccountType(account.trim()) === "PHONE" ? <Phone size={16} /> : <Mail size={16} />;
  const title = mode === "login" ? copy.titleLogin : mode === "register" ? copy.titleRegister : copy.titleForgot;
  const body = mode === "forgot" ? copy.forgotBody : bodyOverride || copy.body;
  const passwordLabel = mode === "forgot" ? copy.newPassword : copy.password;
  const passwordPlaceholder = mode === "forgot" ? copy.newPasswordPlaceholder : copy.passwordPlaceholder;
  const submitLabel = mode === "login" ? copy.loginTab : mode === "register" ? copy.registerTab : copy.resetTab;

  const loginMemoryOptions = mode === "login" && !canvasSession ? (
    <div className="auth-memory-options">
      <label className="auth-memory-check">
        <input checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} type="checkbox" />
        <span>{copy.autoLogin}</span>
      </label>
      <label className="auth-memory-check">
        <input
          checked={rememberPassword}
          onChange={(event) => {
            setRememberPassword(event.target.checked);
            localStorage.setItem(REMEMBER_PASSWORD_ENABLED_KEY, String(event.target.checked));
          }}
          type="checkbox"
        />
        <span>{copy.rememberPassword}</span>
      </label>
    </div>
  ) : null;

  const verificationCodeField = mode !== "login" ? (
    <label>
      <span>{copy.code}</span>
      <div className="input-shell auth-code-shell">
        <ShieldCheck size={16} />
        <input value={verificationCode} onChange={(event) => setVerificationCode(event.target.value)} type="text" inputMode="numeric" required placeholder={copy.codePlaceholder} />
        <button className="code-send-button" type="button" onClick={handleSendCode} disabled={sendingCode}>
          <Send size={15} />
          {sendingCode ? copy.sendingCode : copy.sendCode}
        </button>
      </div>
    </label>
  ) : null;

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label={copy.close}>
          <X size={18} />
        </button>
        <div className="auth-heading">
          <div className="eyebrow">{copy.eyebrow}</div>
          <h2 id="auth-title">{title}</h2>
          <p>{body}</p>
        </div>
        <form className="auth-form" onSubmit={handleSubmit}>
          <label>
            <span>{accountLabel}</span>
            <div className="input-shell">
              {accountIcon}
              <input
                value={account}
                onChange={(event) => setAccount(event.target.value)}
                type={mode === "register" ? "email" : "text"}
                autoComplete={mode === "register" ? "email" : "username"}
                required
                placeholder={mode === "register" ? copy.emailPlaceholder : copy.accountPlaceholder}
              />
            </div>
          </label>
          {mode === "register" ? (
            <label>
              <span>{copy.nickname}</span>
              <div className="input-shell">
                <UserRound size={16} />
                <input value={nickname} onChange={(event) => setNickname(event.target.value)} type="text" autoComplete="nickname" placeholder={copy.nicknamePlaceholder} />
              </div>
            </label>
          ) : null}
          {mode === "forgot" ? verificationCodeField : null}
          <label>
            <span>{passwordLabel}</span>
            <div className="input-shell">
              <Lock size={16} />
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type={passwordVisible ? "text" : "password"}
                autoComplete={mode === "login" && rememberPassword ? "current-password" : "new-password"}
                minLength={8}
                required
                placeholder={passwordPlaceholder}
              />
              <button className="password-visibility-button" type="button" onClick={() => setPasswordVisible((current) => !current)} aria-label={passwordVisible ? copy.hidePassword : copy.showPassword}>
                {passwordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>
          {loginMemoryOptions}
          {mode !== "login" ? (
            <>
              <label>
                <span>{copy.confirmPassword}</span>
                <div className="input-shell">
                  <Lock size={16} />
                  <input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} type={confirmPasswordVisible ? "text" : "password"} autoComplete="new-password" minLength={8} required placeholder={copy.confirmPlaceholder} />
                  <button className="password-visibility-button" type="button" onClick={() => setConfirmPasswordVisible((current) => !current)} aria-label={confirmPasswordVisible ? copy.hideConfirmPassword : copy.showConfirmPassword}>
                    {confirmPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>
              {mode === "register" ? verificationCodeField : null}
            </>
          ) : null}
          {codeMessage ? <div className="form-success">{codeMessage}</div> : null}
          {error ? <div className="form-error">{error}</div> : null}
          {mode !== "forgot" ? (
            <label className="agreement-check">
              <input checked={agreementAccepted} onChange={(event) => setAgreementAccepted(event.target.checked)} type="checkbox" />
              <span>
                {copy.agreementText}{" "}
                <button type="button" className="agreement-link" onClick={() => setAgreementModal("terms")}>
                  《{copy.terms}》
                </button>
                {copy.agreementAnd}
                <button type="button" className="agreement-link" onClick={() => setAgreementModal("privacy")}>
                  《{copy.privacy}》
                </button>
              </span>
            </label>
          ) : null}
          <button className="button button-primary" type="submit" disabled={loading}>
            {loading ? copy.working : submitLabel}
          </button>
          {mode === "login" ? (
            <button className="auth-switch-link" type="button" onClick={() => setModeSafely("forgot")}>
              {copy.forgotLink}
            </button>
          ) : null}
          {!canvasSession ? (
            <button className="auth-switch-link" type="button" onClick={() => setModeSafely(mode === "login" ? "register" : "login")}>
              {mode === "login" ? copy.registerLink : copy.loginLink}
            </button>
          ) : null}
        </form>
      </div>
      {agreementModal ? (
        <LegalDocumentModal kind={agreementModal} language={language} closeLabel={copy.close} onClose={() => setAgreementModal(null)} />
      ) : null}
    </div>
  );
}

function inferAccountType(account: string): "EMAIL" | "PHONE" {
  return account.includes("@") ? "EMAIL" : "PHONE";
}

function isEmailAccount(account: string) {
  return account.includes("@");
}
